/**
 * Boolean Field Component
 * 
 * Renders a checkbox for boolean properties
 */

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import type { InferredPropertySchema } from '@/types/inferred-schema';
import { HelpCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface BooleanFieldProps {
  property: InferredPropertySchema;
  value: boolean;
  onChange: (value: boolean) => void;
  error?: string;
}

export function BooleanField({ property, value, onChange, error }: BooleanFieldProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-2">
        <Checkbox
          id={property.key}
          checked={value ?? false}
          onCheckedChange={(checked) => onChange(checked as boolean)}
        />
        <Label
          htmlFor={property.key}
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          {property.label}
          {property.required && <span className="text-destructive ml-1">*</span>}
        </Label>
        {property.helpText && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-sm">{property.helpText}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      {property.description && (
        <p className="text-sm text-muted-foreground ml-6">{property.description}</p>
      )}
      {error && <p className="text-sm text-destructive ml-6">{error}</p>}
    </div>
  );
}
